import React, { useState, useEffect } from 'react';
import { useLocation } from "react-router-dom";
import { flushSync } from 'react-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DiscountIcon from '@mui/icons-material/LocalOffer';
import PaymentIcon from '@mui/icons-material/Payment';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useKioskContext } from '../context/KioskContext';
import CustomerInfoDialog from '../dialog/CustomerInfoDialog';
import ComplimentaryDialog from "../dialog/ComplimentaryDialog";
import { validateOTP } from "../../../component/OTPValidator";
import { getAuthCredentials } from "../../../utils/auth";

const { user } = getAuthCredentials();

const CartReview = ({
  orderType,
  cartItems,
  onUpdateQuantity,
  onRemoveItem,
  onBack,
  onProceedToPayment,
  posId,
  onAddItem,
  masterMenu,
  brandLogo,
  brandLogoLoading,
}) => {
  const { translate, formatPrice, discountDetails,  discountDetails2, pushExtraDiscount, updateDiscountDetails, resetDiscount, voucherProducts, setVoucherProducts, clearVoucherProducts, changeOrderType } = useKioskContext();
  const [showDiscount, setShowDiscount] = useState(false);
  const [discountType, setDiscountType] = useState(discountDetails?.discountType || 'None');
  const [voucherCode, setVoucherCode] = useState(discountDetails?.voucherCode || '');
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [finalTotal, setFinalTotal] = useState(0);
  const [triggerRecalc, setTriggerRecalc] = useState(0);
  const [customerId, setCustomerId] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [isVoucherError, setIsVoucherError] = useState(false);
  const [voucherApplied, setVoucherApplied] = useState(false);
  const [voucherMinimum, setVoucherMinimum] = useState(null);
  const [serviceChargeRate, setServiceChargeRate] = useState(null);
  const [serviceChargeAmount, setServiceChargeAmount] = useState(0);
  const [employeeName, setEmployeeName] = useState("");
  const [compDialogOpen, setCompDialogOpen] = useState(false);
  const isComplimentary = (orderType === "Complimentary");

  const location = useLocation();
  const showAltLogo = location.pathname.includes("ebcbaguio");



  useEffect(() => {
    if (!posId) return;
  
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/v1/api/pos/service-charge-public?posId=${posId}`)
      .then(res => {
        const rate = res.data?.serviceCharge || 0;
        setServiceChargeRate(rate);
      })
      .catch(err => {
        console.error("Failed to fetch service charge:", err);
      });
  }, [posId]);
  
   // Calculate subtotal - ensure we're using the proper price format
   const subtotal = cartItems.reduce((sum, item) => sum + item.totalPrice, 0);

   // Identify highest menu and beverage
   const isBev = (it) => (it.menuTags || []).some(t => /bev|drink/i.test(String(t)));

   // Reset badges (keep non-transferable like Voucher/Diplomat if present later)
   cartItems.forEach(itm => {
     if (itm.discounts && itm.discounts.length) {
       itm.applyDiscount = true;
       return;
     }
     itm.applyDiscount = false;
     itm.discounts = [];
   });
   
   // Build pools, highest unit price first (price ÷ pax)
   const mealPool = [...cartItems]
     .filter(i => !isBev(i))
     .sort((a, b) => b.totalPrice / (b.menuPax || 1) - a.totalPrice / (a.menuPax || 1));
   
   const bevPool = [...cartItems]
     .filter(i => isBev(i))
     .sort((a, b) => b.totalPrice / (b.menuPax || 1) - a.totalPrice / (a.menuPax || 1));
   
   // helper: tag one pax of `type` onto first item in pool that still has capacity
   const remainingCap = (target) => {
     const capacity = (target.menuPax || 1) * (target.quantity || 1);
     const used = (target.discounts || []).reduce((s, d) => s + (d.pax || 1), 0);
     return capacity - used;
   };
   
   const markItem = (target, type) => {
     if (remainingCap(target) > 0) {
       target.applyDiscount = true;
       target.discounts = target.discounts || [];
       target.discounts.push({ type, pax: 1 });
       return true;
     }
     return false;
   };
   
   const assignOne = (pool, type) => {
     for (const it of pool) {
       if (markItem(it, type)) return true;
     }
     return false;
   };
   
   // remove transferable types before recalculating (keeps Voucher/Diplomat intact)
   const transferable = ["Senior", "PWD", "SoloParent", "Athlete"];
   cartItems.forEach((itm) => {
     if (!itm.discounts) return;
     itm.discounts = itm.discounts.filter(d => !transferable.includes(d.type));
     itm.applyDiscount = itm.discounts.length > 0;
   });
   
   // Build how many PERSONS (tickets) we have per type
   const wanted = {};
   if (discountDetails.discountType !== "None") {
     wanted[discountDetails.discountType] = 1;
   }
   (discountDetails2 || []).forEach(({ type }) => {
     wanted[type] = (wanted[type] || 0) + 1;
   });
   
   // **PAIRING LOGIC**: for each person, try 1 meal + 1 drink
   Object.entries(wanted).forEach(([type, count]) => {
     for (let i = 0; i < count; i++) {
       const mealOk = assignOne(mealPool, type);
       const bevOk  = assignOne(bevPool, type);
   
       if (!mealOk && !bevOk) break;
     }
   });
  
  // Voucher / Diplomat blanket-apply
  if (['Voucher', 'Diplomat'].includes(discountDetails.discountType)) {
    cartItems.forEach(i => {
      const totalPax = (i.menuPax > 0 ? i.menuPax : 1) * (i.quantity || 1);
  
      i.applyDiscount = true;
  
      const existing = i.discounts.find(d => d.type === discountDetails.discountType);
      if (existing) {
        existing.pax = totalPax;                  
      } else {
        i.discounts.push({ type: discountDetails.discountType, pax: totalPax });
      }
    });
  }
 
   // Compute VAT totals correctly based on flagged discounts
   let vatSales = 0;          // vatable net sales (12 % VAT already stripped)
   let vatExemptSales = 0;    // sales that became VAT-exempt
   let vatRecoverable = 0;    // VAT still collected on vatable sales
   let discount = 0;          // total peso discount to show on receipt
   
   // helper → split a gross amount (price that already includes 12 % VAT)
   const splitVat = (gross) => {
     const vat = gross * (12 / 112);
     return { net: gross - vat, vat };
   };
   
   cartItems.forEach((item) => {
     // if no discounts tagged on this item, treat entire price as vatable
     if (!item.discounts || item.discounts.length === 0) {
       const { net, vat } = splitVat(item.totalPrice);
       vatSales += net;
       vatRecoverable += vat;
       return;
     }
   
     const paxPerServe = item.menuPax > 0 ? item.menuPax : 1;
     const totalPax    = paxPerServe * (item.quantity || 1); 
     const unitPrice = item.totalPrice / totalPax;   // gross for one pax
     let remainingPax = totalPax;
   
     // ---------- handle every discount “ticket” attached to the item ----
     item.discounts.forEach(({ type, pax = 1 }) => {
      const gross = unitPrice * pax;
       const { net, vat } = splitVat(gross);
   
       switch (type) {
         case "Senior":
         case "PWD":
           vatExemptSales += net;            // VAT removed
           discount += net * 0.20;           // 20 % off after VAT removal
           break;
   
         case "SoloParent":
           vatExemptSales += net;
           discount += net * 0.10;           // 10 % off
           break;
   
         case "Athlete":
           vatSales += net;                  // still vatable
           vatRecoverable += vat;
           discount += net * 0.20;           // 20 % off net
           break;
   
         case "Diplomat":
           vatExemptSales += net;            // entire VAT removed
           discount += vat;                  // discount equals the VAT removed
           break;
   
         case "Voucher":
           // voucher peso amount handled globally below
           vatSales += net;
           vatRecoverable += vat;
           break;
   
         default:
           // unknown type ⇒ treat as normal sale
           vatSales += net;
           vatRecoverable += vat;
       }
   
       remainingPax -= pax;                    // one pax consumed by this ticket
     });
   
     // ---------- any pax left without discount --------------------------
     if (remainingPax > 0) {
       const grossRemaining = unitPrice * remainingPax;
       const { net, vat } = splitVat(grossRemaining);
       vatSales += net;
       vatRecoverable += vat;
     }
   });
   
   /* voucher peso discount (if present) is stored in discountDetails */
   if (discountDetails.discountType === "Voucher") {
     discount += discountDetails.discount || 0;
   }
   
   const vatAmount = vatRecoverable;
   const subtotalExcludingVAT = vatSales + vatExemptSales;
   const vatExemptBase = vatExemptSales;
  
  // Calculate discount amount based on type
  const calculateDiscount = () => discount;
  
  // Calculate final total with discount based on type (following Cart.js)
  let finalVatAmount = vatAmount;

  useEffect(() => {
    const main = discountDetails.discountType;   // ← true primary
  
    const isDineIn = orderType === "Dine-In";
    const sc = isDineIn ? (subtotal * serviceChargeRate) / 100 : 0;
  
    let total = 0;
    if (main === "Athlete") {
      total = subtotalExcludingVAT - discount + vatAmount + sc;
    } else if (["PWD", "Senior", "SoloParent"].includes(main)) {
      total = subtotalExcludingVAT + vatAmount - discount + sc;
    } else {
      total = subtotal - discount + sc;
    }
  
    setFinalTotal(total);
    setServiceChargeAmount(sc);
    updateDiscountDetails({
      ...discountDetails,
      discount,
      finalTotal: total,
    });
  }, [
    orderType,
    discountDetails.discountType,          // ← watch the real primary
    subtotal,
    subtotalExcludingVAT,
    vatAmount,
    discount,
    serviceChargeRate,
    triggerRecalc,
  ]);
  
  // Cart items should be grouped by item name ignore if there are options
  // const groupedCartItems = cartItems.reduce((acc, item) => {
  //   if (!acc[item.name]) {
  //     acc[item.name] = { ...item, quantity: 0, totalPrice: 0 };
  //   }
  //   acc[item.name].quantity += item.quantity;
  //   acc[item.name].totalPrice += item.totalPrice;
  //   return acc;
  // }, {});
  
  // // Convert grouped cart items back to array
  // const cartItemsArray = Object.values(groupedCartItems); 

  // cartItems = cartItemsArray;

  // Toggle discount section
  const toggleDiscount = () => {
    setShowDiscount(!showDiscount);
  };
  
  // Handle discount type change
  // const handleDiscountTypeChange = (e) => {
  //   const selectedDiscountType = e.target.value;
  //   if (selectedDiscountType === 'None') return;
  
  //   // If there is still no main discount, store here;
  //   // otherwise append to the extra-discounts array
  //   if (discountDetails.discountType === 'None') {
  //     updateDiscountDetails({
  //       ...discountDetails,
  //       discountType: selectedDiscountType
  //     });
  //   } else {
  //     pushExtraDiscount({ type: selectedDiscountType, pax: 1 });
  //   }
  
  //   setDiscountType(selectedDiscountType);
  //   setTriggerRecalc((p) => p + 1);
  
  //   if (selectedDiscountType !== 'Voucher') {
  //     setCustomerDialogOpen(true);
  //   }
  // };
  
  // Reset discount locally and in context
  const purgeTypeFromItems = (type) => {
    cartItems.forEach((itm) => {
      if (!itm.discounts || itm.discounts.length === 0) return;
  
      itm.discounts = itm.discounts.filter((d) => d.type !== type);
      itm.applyDiscount = itm.discounts.length > 0;   // green badge on/off
    });
  };

  const purgeAllDiscountsIfEmpty = () => {
    const hasTickets =
      (discountDetails.discountType !== "None") ||
      (discountDetails2 && discountDetails2.length);
  
    if (hasTickets) return;
  
    cartItems.forEach((itm) => {
      itm.discounts = [];
      itm.applyDiscount = false;
    });
  };

  const removeVoucherArtifacts = () => {
    cartItems.forEach((itm) => {
      if (!itm.discounts) {
        itm.applyDiscount = false;
        return;
      }
      itm.discounts = itm.discounts.filter((d) => d.type !== "Voucher");
      itm.applyDiscount = itm.discounts.length > 0;
    });
  
    cartItems
      .filter((i) => i.voucherAdded)
      .forEach((i) => onRemoveItem(i.id));
  
    clearVoucherProducts();
  };

  const handleResetDiscount = (type) => {
    if (!type) return;

    purgeTypeFromItems(type);

    const extras = [...(discountDetails2 ?? [])];
    const lastIdx = extras.map((d) => d.type).lastIndexOf(type);

    if (lastIdx !== -1) {
      extras.splice(lastIdx, 1);                 // remove from extras
      pushExtraDiscount(extras);
    } else if (discountDetails.discountType === type) {
      if (extras.length) {
        const [next, ...rest] = extras;          // promote next extra
        updateDiscountDetails({
          ...discountDetails,
          discountType: next.type,
        });
        pushExtraDiscount(rest);
      } else {
        updateDiscountDetails({ ...discountDetails, discountType: "None" });
      }
    }

    purgeAllDiscountsIfEmpty();

    setDiscountType(
      discountDetails.discountType === type && extras.length
        ? extras[0]?.type
        : discountDetails.discountType === type
        ? "None"
        : discountDetails.discountType
    );
    setTriggerRecalc((p) => p + 1);
  };

  // Handle apply voucher
  const needsOTP = (voucher) =>
    voucher?.voucherRoleType &&
    voucher.voucherRoleType !== "None" &&
    (voucher.voucherType || "").toLowerCase() !== "product deduction";
  
  const handleApplyVoucher = async () => {
    if (!voucherCode.trim()) {
      Swal.fire({ icon: "error", title: "Voucher Error", text: "Please enter a voucher code." });
      setIsVoucherError(true);
      return;
    }

    try {
      const userId     = user?._id || user?.id || user?.userId || null;
      const userRoleId = user?.roleId || user?.userRoleId || user?.role?._id || null;

      const payload = {
        voucherCode,
        subtotal: subtotal * 100,
        posId,
        ...(userId ? { userId } : {}),
        ...(userRoleId ? { userRoleId } : {}),
      };

      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/vouchers/apply-voucher`,
        payload
      );

      const { discount: appliedDiscount, message, voucher } = data;

      if (message && appliedDiscount === 0) {
        Swal.fire({ icon: "error", title: "Voucher Error", text: message });
        setIsVoucherError(true);
        resetDiscount();
        return;
      }

      if (needsOTP(voucher)) {
        const label = voucher.voucherRoleName || "Employee";
        try {
          const res = await validateOTP(`Authorize ${label} Voucher ${voucherCode}`);
          const ok  = res === true || res?.status === "success";
          if (!ok) {
            Swal.fire({ icon: "info", title: "OTP required", text: "Voucher not applied." });
            setIsVoucherError(true);
            resetDiscount();
            return;
          }
        } catch {
          Swal.fire({ icon: "info", title: "OTP cancelled", text: "Voucher not applied." });
          setIsVoucherError(true);
          resetDiscount();
          return;
        }
      }

      Swal.fire({
        icon: "success",
        title: "Voucher Applied",
        text: `Voucher applied! Discount: ${formatPrice(appliedDiscount / 100)}`
      });

      if (voucher?.voucherType === "Product Deduction" && Array.isArray(voucher.products)) {
        const voucherItems = voucher.products.map((prod) => {
          const ref = (masterMenu ?? []).find(
            (m) => m._id === prod.menuItemId || m.menuItemId === prod.menuItemId
          ) || {};
          const basePriceCents = ref.basePrice != null ? ref.basePrice : (prod.price ?? 0) * 100;

          return {
            ...ref,
            id: `voucher-${prod.menuItemId}-${Date.now()}-${Math.random()}`,
            menuItemId: prod.menuItemId,
            menuName: ref.menuName || prod.menuName,
            basePrice: basePriceCents,
            menuImage: ref.menuImage,
            menuTags: ref.menuTags || [],
            voucherAdded: true,
            quantity: prod.quantity || 1,
            selectedOptions: [],
            totalPrice: (basePriceCents / 100) * (prod.quantity || 1),
          };
        });

        onAddItem(voucherItems);
        setVoucherProducts(voucher.products || []);
      }

      setVoucherMinimum((voucher?.minimumDeduct ?? 0) / 100);

      updateDiscountDetails({
        discountType: "Voucher",
        voucherCode,
        discount: appliedDiscount / 100,
      });

      setIsVoucherError(false);
      setVoucherApplied(true);
    } catch (err) {
      console.error("Error applying voucher:", err);
      Swal.fire({
        icon: "error",
        title: "Voucher Error",
        text: err.response?.data?.message || "Error applying voucher."
      });
      setIsVoucherError(true);
      resetDiscount();

      removeVoucherArtifacts();
      purgeAllDiscountsIfEmpty();
      setTriggerRecalc(p => p + 1);
    }
  };

  const qtyFor = (menuItemId, nextItems = cartItems) =>
    nextItems.filter(i => i.menuItemId === menuItemId)
             .reduce((s, i) => s + i.quantity, 0);
  
  const voucherNeeds = (menuItemId) =>
    voucherProducts.find(p => p.menuItemId === menuItemId)?.quantity ?? 0;
  
  const confirmVoucherCancel = (cb) =>
    Swal.fire({
      icon: "warning",
      title: "Removing this item will cancel the voucher",
      text: "Do you want to proceed?",
      showCancelButton: true,
      confirmButtonText: "Yes, remove item",
      cancelButtonText: "No",
    }).then((r) => {
      if (r.isConfirmed) {
        updateDiscountDetails({ discountType: "None", discount: 0, voucherCode: "" });
        clearVoucherProducts();
        pushExtraDiscount([]);
        removeVoucherArtifacts();
        purgeAllDiscountsIfEmpty();
        setVoucherApplied(false);
        setVoucherMinimum(null);
        cb();
      }
  });

  const eligibleSubtotalNow = () =>
    cartItems.filter(i => !i.voucherAdded).reduce((s, i) => s + i.totalPrice, 0);

  const safeRemoveLine = (item) => {
    const eligibleNow = eligibleSubtotalNow();
    const nextEligible = eligibleNow - (item.voucherAdded ? 0 : item.totalPrice);
  
    if (
      discountDetails.discountType === "Voucher" &&
      voucherApplied &&
      (voucherMinimum ?? 0) > 0 &&
      nextEligible < voucherMinimum
    ) {
      return confirmVoucherCancel(() => {
        if (!item.voucherAdded) onRemoveItem(item.id);
      });
    }
  
    const leftAfter = qtyFor(item.menuItemId) - item.quantity;
    if (
      discountDetails.discountType === "Voucher" &&
      voucherNeeds(item.menuItemId) > leftAfter
    ) {
      return confirmVoucherCancel(() => {
        if (!item.voucherAdded) onRemoveItem(item.id);
      });
    }
  
    onRemoveItem(item.id);
  };
    
  const safeDecrement = (item) => {
    if (item.quantity === 1) return safeRemoveLine(item);
  
    const unitPrice = item.totalPrice / item.quantity;
    const eligibleNow = eligibleSubtotalNow();
    const nextEligible = eligibleNow - (item.voucherAdded ? 0 : unitPrice);
  
    if (
      discountDetails.discountType === "Voucher" &&
      voucherApplied &&
      (voucherMinimum ?? 0) > 0 &&
      nextEligible < voucherMinimum
    ) {
      return confirmVoucherCancel(() => {
        if (!item.voucherAdded) onUpdateQuantity(item.id, item.quantity - 1);
      });
    }
  
    const newQty = item.quantity - 1;
    const leftAfter = qtyFor(item.menuItemId) - 1;
    if (
      discountDetails.discountType === "Voucher" &&
      voucherNeeds(item.menuItemId) > leftAfter
    ) {
      return confirmVoucherCancel(() => {
        if (!item.voucherAdded) onUpdateQuantity(item.id, newQty);
      });
    }
  
    onUpdateQuantity(item.id, newQty);
  };
  

    const handleIncrease = (item) => {
      if (item.applyDiscount) {
        const unitPrice = item.totalPrice / item.quantity; 
        onAddItem([
          {
            ...item,
            id: `${item.id}-extra-${Date.now()}`,
            quantity: 1,
            totalPrice: unitPrice,
            applyDiscount: false,
            discounts: [],
          },
        ]);
        return;
      }
    
      onUpdateQuantity(item.id, item.quantity + 1);
    };
  
  // Customer info dialog customer creation handler
  useEffect(() => {
    window.onCustomerCreated = (id, ordId) => {
      setCustomerId(id);
      setOrderId(ordId);
  
      const currentDiscount = calculateDiscount();
      const mainType =
        discountDetails.discountType !== "None"
          ? discountDetails.discountType   
          : discountType;                  
  
      let computedTotal = 0;
      if (mainType === "Athlete") {
        computedTotal =
          subtotalExcludingVAT - currentDiscount + vatAmount + serviceChargeAmount;
      } else if (["PWD", "Senior", "SoloParent"].includes(mainType)) {
        computedTotal =
          subtotalExcludingVAT + vatAmount - currentDiscount + serviceChargeAmount;
      } else {
        computedTotal = subtotal - currentDiscount + serviceChargeAmount;
      }
  
      if (!discountDetails.customerId) {
        updateDiscountDetails({
          ...discountDetails,
          discountType : mainType,
          customerId   : id,
          orderId      : ordId,
          discount     : currentDiscount,
          finalTotal   : computedTotal,
          extraCustomers: [],               
        });
      } else {
        updateDiscountDetails({
          ...discountDetails,
          extraCustomers: [
            ...(discountDetails.extraCustomers ?? []),
            { customerId: id, orderId: ordId },
          ],
        });
      }
    };
  
    return () => {
      delete window.onCustomerCreated;
    };
  }, [discountType, discountDetails.discountType, discountDetails.extraCustomers]);

  const hasVoucherOrDiplomatSelected =
    ['Voucher', 'Diplomat'].some(t =>
      discountDetails.discountType === t ||
      (discountDetails2 ?? []).some(d => d.type === t)
    );

  const hasRegularDiscountSelected =
    (discountDetails.discountType !== 'None' &&
    !['Voucher', 'Diplomat'].includes(discountDetails.discountType)) ||
    (discountDetails2 ?? []).some(d => !['Voucher', 'Diplomat'].includes(d.type));

  const selectionAllowed = () => {
    if (!discountType || discountType === 'None') return false;        // nothing chosen
    if (hasVoucherOrDiplomatSelected) return false;                    // blanket already present
    if (['Voucher', 'Diplomat'].includes(discountType) && hasRegularDiscountSelected) return false;
    return true;                                                       // valid pick
  };

  const clearBlanketDiscount = (type) => {
    cartItems.forEach((itm) => {
      if (!itm.discounts) return;
      itm.discounts = itm.discounts.filter((d) => d.type !== type);
      itm.applyDiscount = itm.discounts.length > 0;
    });
    setTriggerRecalc((p) => p + 1);
  };

  const hasRemainingDiscountSlots = () => {
    return cartItems.some(item => {
      const capacity = (item.menuPax > 0 ? item.menuPax : 1) * (item.quantity || 1);
      const usedPax = (item.discounts || []).reduce((sum, d) => sum + (d.pax || 1), 0);
      return usedPax < capacity;
    });
  };

  const splitVariantForDiscount = (discType) => {
    if (["Voucher", "Diplomat", "None"].includes(discType)) return; 
  
    const sorted = (isBev) =>
      [...cartItems]
        .filter(i => (isBev ? i.menuTags?.includes("beverage") : !i.menuTags?.includes("beverage")))
        .sort((a, b) => b.totalPrice / (b.menuPax || 1) - a.totalPrice / (a.menuPax || 1));
  
    const trySplit = (target) => {
      if (!target) return false;
      if ((target.menuPax || 1) > 1) return false;        
      if (target.quantity <= 1) return false;               
  
      const unitPrice = target.totalPrice / target.quantity;
      onUpdateQuantity(target.id, target.quantity - 1);
  
      onAddItem([
        {
          ...target,
          id: `${target.id}-disc-${Date.now()}`,
          quantity: 1,
          totalPrice: unitPrice,
          applyDiscount: true,
          discounts: [{ type: discType, pax: 1 }],
        },
      ]);
  
      return true;
    };
  
    if (!trySplit(sorted(false)[0])) {
      trySplit(sorted(true)[0]);
    }
  };

  return (
    <Box sx={{ 
      height: '100vh', 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: '#f5f5f5'
    }}>
      {/* Header with logo */}
      <Box sx={{ 
        p: 2, 
        bgcolor: 'white', 
        boxShadow: 1,
        display: 'flex',
        alignItems: 'center'
      }}>
        <IconButton 
          onClick={onBack} 
          color="primary"
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon fontSize="large" />
        </IconButton>
        
        <img
          src={
            brandLogo ||
            (showAltLogo ? "/assets/images/logo2.png" : "/assets/images/logo.png")
          }
          alt="Brand"
          style={{ height: '50px' }}
          onError={(e) => {
            e.currentTarget.src = showAltLogo
              ? "/assets/images/logo2.png"
              : "/assets/images/logo.png";
          }}
        />
        
        <Typography variant="h5" sx={{ ml: 2, flexGrow: 1 }}>
          {translate('Review Your Order', {
            en: 'Review Your Order',
            zh: 'u67e5u770bu60a8u7684u8ba2u5355',
            th: 'u0e17u0e1au0e17u0e27u0e19u0e04u0e33u0e2au0e31u0e48u0e07u0e0bu0e37u0e49u0e2du0e02u0e2du0e07u0e04u0e38u0e13',
            tl: 'Suriin ang Iyong Order'
          })}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => changeOrderType(orderType === "Dine-In" ? "Take-Out" : "Dine-In")}
          >
            {orderType}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Switch Order Type
          </Typography>
        </Box>
      </Box>

      {/* Cart Items */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2, minWidth: 50, flexShrink: 0 }}>
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2, minWidth: 50, flexShrink: 0 }}>
          <Typography variant="h5" gutterBottom>
            {translate('Order Items', {
              en: 'Order Items',
              zh: 'u8ba2u5355u9879u76ee',
              th: 'u0e23u0e32u0e22u0e01u0e32u0e23u0e2au0e31u0e48u0e07u0e0bu0e37u0e49u0e2d',
              tl: 'Mga Item sa Order'
            })}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {cartItems.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: 'center', py: 3 }}>
              {translate('Your cart is empty', {
                en: 'Your cart is empty',
                zh: 'u60a8u7684u8d2du7269u8f66u662fu7a7au7684',
                th: 'u0e23u0e16u0e40u0e02u0e47u0e19u0e02u0e2du0e07u0e04u0e38u0e13u0e27u0e48u0e32u0e07u0e40u0e1bu0e25u0e48u0e32',
                tl: 'Ang iyong cart ay walang laman'
              })}
            </Typography>
          ) : (
            <Box>
              {cartItems.map((item) => (
                <Paper 
                  key={item.id} 
                  elevation={1} 
                  sx={{ 
                    p: 2, 
                    mb: 2, 
                    display: 'flex', 
                    alignItems: 'center',
                    borderRadius: 1,
                    bgcolor: '#fafafa' 
                  }}
                >
                  {/* Item image */}
                  <Box sx={{ width: 80, height: 80, mr: 2, borderRadius: 1, overflow: 'hidden' }}>
                    <img 
                      src={item.menuImage || '/assets/images/default-food.jpg'} 
                      alt={item.menuName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                  
                  {/* Item details */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "medium",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {item.chiliLevelData
                        ? `(${item.chiliLevelData.chiliLevelName}) ${item.menuName}`
                        : item.menuName}
                      {item.isTakeoutFee && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            border: "1px dashed",
                            borderColor: "text.disabled",
                            borderRadius: "4px",
                            px: 0.5,
                            ml: 1,
                          }}
                        >
                          Auto-added for Take-Out
                        </Typography>
                      )}
                      {discountDetails.discountType !== "Voucher" && item.applyDiscount && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            color: "green",
                            fontWeight: "bold",
                            border: "1px solid green",
                            borderRadius: "4px",
                            px: 0.5,
                            ml: 1,
                          }}
                      >
                        Discount Applied
                      </Typography>
                      )}
                    </Typography>
                    {/* Item options */}
                    {(item.selectedOptions && item.selectedOptions.length > 0) && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                          Options:
                        </Typography>
                        {item.selectedOptions.map((option, index) => (
                          <Box key={index} sx={{ ml: 1 }}>
                            {Array.isArray(option.choices) ? (
                              option.choices.map((choice, idx) => {
                                const choiceName = typeof choice === 'object' ? choice.name : String(choice);
                                const choicePrice = typeof choice === 'object' ? choice.price : 0;
                                
                                return (
                                  <Typography key={idx} variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                                    • {choiceName}
                                    {choicePrice > 0 && (
                                      <> (+{formatPrice(choicePrice)})</>
                                    )}
                                  </Typography>
                                );
                              })
                            ) : (
                              <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                                • {typeof option.choice === 'object' ? option.choice.name : option.choice}
                                {typeof option.choice === 'object' && option.choice.price > 0 && (
                                  <> (+{formatPrice(option.choice.price)})</>
                                )}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    <Typography variant="body2" color="primary" fontWeight="medium">
                      {formatPrice(item.totalPrice / item.quantity)} each
                    </Typography>
                  </Box>
                  
                  {/* Quantity controls */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    <IconButton 
                      size="small" 
                      onClick={() => !item.isTakeoutFee && safeDecrement(item)}
                      disabled={item.quantity <= 1 || item.isTakeoutFee}
                    >
                      <RemoveIcon />
                    </IconButton>
                    <Typography sx={{ mx: 1, minWidth: 20, textAlign: 'center' }}>
                      {item.quantity}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => !item.isTakeoutFee && handleIncrease(item)}
                      disabled={item.isTakeoutFee}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  
                  {/* Item total price */}
                  <Box sx={{ minWidth: 100, textAlign: 'right', mr: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {formatPrice(item.totalPrice)}
                    </Typography>
                  </Box>
                  
                  {/* Remove button */}
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={() => safeRemoveLine(item)}
                    disabled={item.isTakeoutFee}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Paper>
              ))}
            </Box>
          )}
        </Paper>
      </Box>

      {/* Order Summary */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3,
          borderTopLeftRadius: 16, 
          borderTopRightRadius: 16,
          boxShadow: '0px -4px 10px rgba(0, 0, 0, 0.1)',
          minWidth: 50,
          flexShrink: 0
        }}
      >
        {/* Discount Section */}
        {!isComplimentary && (
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {translate("Discount", { en: "Discount" })}
          </Typography>

          {/* LIST OF CURRENT DISCOUNTS */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {[
              ...(discountDetails.discountType !== "None"
                ? [{ type: discountDetails.discountType }]
                : []),
              ...(discountDetails2 ?? []),
            ].map((d, idx) => (
              <Button
                key={`${d.type}-${idx}`}
                size="small"
                variant="outlined"
                color="success"
                endIcon={<DeleteOutlineIcon />}
                onClick={() => {
                  purgeTypeFromItems(d.type);

                  const extras    = [...(discountDetails2 ?? [])];
                  const custExtra = [...(discountDetails.extraCustomers ?? [])];
                
                  const isPrimary = idx === 0 && discountDetails.discountType !== 'None';
                  const extraIdx  = idx - (discountDetails.discountType !== 'None' ? 1 : 0);

                  purgeTypeFromItems(d.type);
                
                  if (isPrimary) {
                  } else {
                    extras.splice(extraIdx, 1);
                    custExtra.splice(extraIdx, 1);
                  }
                
                  let nextType   = discountDetails.discountType;
                  let nextCustId = discountDetails.customerId;
                  let nextOrdId  = discountDetails.orderId;
                
                  if (isPrimary) {
                    if (extras.length) {
                      const promoted     = extras.shift();
                      const promotedCust = custExtra.shift();
                      nextType           = promoted.type;
                      nextCustId         = promotedCust?.customerId ?? null;
                      nextOrdId          = promotedCust?.orderId  ?? null;
                    } else {
                      nextType   = 'None';
                      nextCustId = null;
                      nextOrdId  = null;
                    }
                  }
                
                  updateDiscountDetails(
                    nextType === 'None'
                      ? {
                          discount       : 0,
                          discountType   : 'None',
                          voucherCode    : '',
                          voucherDesc    : '',
                          customerId     : null,
                          orderId        : null,
                          extraCustomers : [],
                        }
                      : {
                          ...discountDetails,
                          discountType   : nextType,
                          customerId     : nextCustId,
                          orderId        : nextOrdId,
                          extraCustomers : custExtra,
                        }
                  );

                  if (d.type === 'Voucher') {
                    clearVoucherProducts?.();
                    setVoucherApplied?.(false);
                  }
                
                  pushExtraDiscount(extras);

                  if (d.type === 'Voucher') {
                    removeVoucherArtifacts();
                  }

                  purgeAllDiscountsIfEmpty();
                  setTriggerRecalc(p => p + 1);
                }}
              >
                {d.type}
              </Button>
            ))}

            {![discountDetails.discountType, ...(discountDetails2 ?? [])].length && (
              <Typography variant="body2" color="text.secondary">
                {translate("No discounts yet", { en: "No discounts yet" })}
              </Typography>
            )}
          </Box>

          {/* ADD ANOTHER DISCOUNT */}
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <FormControl fullWidth size="small">
              <InputLabel>{translate("Add discount", { en: "Add discount" })}</InputLabel>
              <Select
                value={discountType || ''}
                displayEmpty
                renderValue={(selected) =>
                  selected ? selected : translate("Add Discount", { en: "Add Discount" })
                }
                onChange={(e) => setDiscountType(e.target.value)}
              >
                <MenuItem value="">
                  <em>{translate("Add Discount", { en: "Add Discount" })}</em>
                </MenuItem>

                {/* regular discounts – disabled once Voucher/Diplomat is chosen */}
                {["PWD", "Senior", "SoloParent", "Athlete"].map((opt) => (
                  <MenuItem
                    key={opt}
                    value={opt}
                    disabled={hasVoucherOrDiplomatSelected}
                  >
                    {opt}
                  </MenuItem>
                ))}

                {/* blanket discounts – disabled if any regular OR another blanket exists */}
                <MenuItem
                  value="Diplomat"
                  disabled={hasRegularDiscountSelected || hasVoucherOrDiplomatSelected}
                >
                  Diplomat
                </MenuItem>
                <MenuItem
                  value="Voucher"
                  disabled={hasRegularDiscountSelected || hasVoucherOrDiplomatSelected}
                >
                  Voucher
                </MenuItem>
              </Select>
            </FormControl>

            {discountType === "Voucher" ? (
              <>
                <TextField
                  size="small"
                  label={translate("Voucher Code", { en: "Voucher Code" })}
                  value={voucherCode}
                  onChange={(e) => setVoucherCode(e.target.value)}
                />
                <Button
                  onClick={handleApplyVoucher}
                  disabled={!voucherCode || !selectionAllowed()}
                  variant="contained"
                >
                  {translate("Apply", { en: "Apply" })}
                </Button>
              </>
            ) : (
              <Button
                disabled={!selectionAllowed()}
                variant="contained"
                startIcon={<DiscountIcon />}
                onClick={() => {
                  if (!selectionAllowed()) return;

                  if (!hasRemainingDiscountSlots()) {
                    Swal.fire({
                      icon: "info",
                      title: "All Orders already discounted",
                      text : "Every eligible pax already has a discount applied.",
                    });
                    return;
                  }

                  if (discountDetails.discountType === 'None') {
                    splitVariantForDiscount(discountType);
                    updateDiscountDetails({ ...discountDetails, discountType });
                  } else {
                    splitVariantForDiscount(discountType);
                    pushExtraDiscount([
                      ...(discountDetails2 ?? []),
                      { type: discountType, pax: 1 }
                    ]);
                  }

                  setTriggerRecalc(p => p + 1);
                  if (discountType !== 'Voucher') setCustomerDialogOpen(true);
                }}
              >
                {translate('Add', { en: 'Add' })}
              </Button>
            )}
          </Box>
        </Paper>
        )}

        {isComplimentary && (
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Employee Details (for Complimentary Meal)
            </Typography>
            <TextField
              fullWidth
              label="Employee Name"
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
            />
          </Paper>
        )}

        {/* Order Totals */}
        <Grid container spacing={1} sx={{ mb: 2, minWidth: 320 }}  >
          <Grid item xs={6}>
            <Typography variant="body1">
              {translate('Subtotal (incl. VAT)', {
                en: 'Subtotal (incl. VAT)',
                zh: '小计 (含增值税)',
                th: 'ยอดรวม (รวมภาษีมูลค่าเพิ่ม)',
                tl: 'Subtotal (kasama ang VAT)',
              })}
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="body1">
              {formatPrice(subtotal)}
            </Typography>
          </Grid>

          {/* dashed separator */}
          <Grid item xs={12}>
            <Divider sx={{ borderStyle: 'dashed', my: 1 }} />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1">
              {translate('Subtotal (excl. VAT)', {
                en: 'Subtotal (excl. VAT)',
                zh: 'u5c0fu8ba1 (u4e0du542bu589eu503cu7a0e)',
                th: 'u0e22u0e2du0e14u0e23u0e27u0e21 (u0e44u0e21u0e48u0e23u0e27u0e21u0e20u0e32u0e29u0e35)',
                tl: 'Subtotal (hindi kasama ang VAT)'
              })}
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="body1">
              {formatPrice(subtotalExcludingVAT)}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body1">
              {translate('12% VAT', {
                en: '12% VAT',
                zh: '12% u589eu503cu7a0e',
                th: '12% u0e20u0e32u0e29u0e35u0e21u0e39u0e25u0e04u0e48u0e32u0e40u0e1eu0e34u0e48u0e21',
                tl: '12% VAT'
              })}
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="body1">
              {formatPrice(finalVatAmount)}
            </Typography>
          </Grid>

          {discount > 0 && (
            <>
              <Grid item xs={6}>
                <Typography variant="body1" color="error">
                  {translate('Discount', {
                    en: 'Discount',
                    zh: 'u6298u6263',
                    th: 'u0e2au0e48u0e27u0e19u0e25u0e14',
                    tl: 'Diskwento'
                  })}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="body1" color="error">
                  -{formatPrice(discount)}
                </Typography>
              </Grid>
            </>
          )}

          {serviceChargeRate > 0 && orderType?.toLowerCase() === "dine-in" && (
            <>
              <Grid item xs={6}>
                <Typography variant="body1">
                  {translate('Service Charge', {
                    en: `${serviceChargeRate}% Service Charge`,
                    zh: `${serviceChargeRate}% 服务费`,
                    th: `${serviceChargeRate}% ค่าบริการ`,
                    tl: `${serviceChargeRate}% Service Charge`,
                  })}
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="body1">
                  {formatPrice(serviceChargeAmount)}
                </Typography>
              </Grid>
            </>
          )}
          <Grid item xs={6}>
            <Typography variant="h6" fontWeight="bold">
              {translate('Total', {
                en: 'Total',
                zh: 'u603bu8ba1',
                th: 'u0e23u0e27u0e21u0e17u0e31u0e49u0e07u0e2bu0e21u0e14',
                tl: 'Kabuuang Halaga'
              })}
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="h6" fontWeight="bold" color="primary">
              {formatPrice(finalTotal)}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
            variant="contained"
            size="large"
            color="primary"
            startIcon={<PaymentIcon />}
            disabled={cartItems.length === 0 || serviceChargeRate === null}
            sx={{ flexGrow: 2, py: 1.5 }}
            onClick={() => {
              if (isComplimentary) {
                if (!employeeName?.trim()) {
                  Swal.fire({ icon: "warning", title: "Employee name required", text: "Please enter employee name." });
                  return;
                }

                validateOTP(`Claim Complimentary Meal + ${employeeName}`)
                .then((res) => {
                  const ok = res === true || res?.status === "success";
                  if (!ok) return;

                  flushSync(() => {
                    updateDiscountDetails({
                      ...discountDetails,
                      discount,
                      finalTotal: 0,
                      complimentary: true,
                      complimentaryEmployeeName: employeeName,
                    });
                  });
                  setCompDialogOpen(true);
                  return;
                })
                .catch((err) => {
                  console.error("OTP validation cancelled or failed:", err);
                });
                return;
              }

              // recompute the exact total on the fly
              const sc =
                orderType === 'Dine-In' ? (subtotal * serviceChargeRate) / 100 : 0;

              const main = discountDetails.discountType;
              const freshTotal =
                main === 'Athlete'
                  ? subtotalExcludingVAT - discount + vatAmount + sc
                  : ['PWD', 'Senior', 'SoloParent'].includes(main)
                  ? subtotalExcludingVAT + vatAmount - discount + sc
                  : subtotal - discount + sc;

              // commit the latest numbers synchronously before navigation
              flushSync(() => {
                updateDiscountDetails({
                  ...discountDetails,         
                  discount,
                  finalTotal: freshTotal,
                });
              });

              // now proceed to the payment screen
              onProceedToPayment();
            }}
          >
            {isComplimentary
              ? 'Claim Complimentary Meal'
              : translate('Pay Now', { en: 'Pay Now' })}
          </Button>
        </Box>
      </Paper>

      {/* Customer Info Dialog for Discount */}
      <CustomerInfoDialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        resetDiscount={handleResetDiscount}
        chosenDiscountType={discountType}
      />

      <ComplimentaryDialog
        open={compDialogOpen}
        onClose={() => setCompDialogOpen(false)}
        posId={posId}
        cartItems={cartItems}
        employeeName={employeeName}
        salesType="Complimentary"
        onSuccess={() => {
          Swal.fire({
            icon: "success",
            title: "Enjoy your meal!",
            timer: 1200,
            showConfirmButton: false,
          });
          setCompDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default CartReview;